/**
 * Plate-geometry checks (R31/D108) — Calder's spec, per D95(4): a plate that
 * declares its `viewpoint` is checked against every room that names it, and
 * the check is ARITHMETIC rather than judgment, because the style block
 * already mandates one camera and the frame's horizontal bands map onto
 * compass directions deterministically once the facing is declared.
 *
 * @remarks
 * Three checks, all on existing machinery:
 *
 * 1. **Referential, both directions, with COMPLETENESS.** Every declared
 *    direction is an exit of every room naming the plate, and every exit of
 *    every such room appears in the declaration — the check that catches an
 *    author who simply forgot an exit costs a plate four tokens, and
 *    `absent` makes "not shown" a decision rather than an omission.
 * 2. **Orientation.** Given `facing`, the expected band for each CARDINAL
 *    exit is derived from one rotation table and compared. What the table
 *    encodes is a READING CONVENTION, not geometry (Calder's D109 reframe —
 *    in a wide frame the far wall spans the whole image, so an opening in it
 *    can geometrically sit anywhere; the rule is "will a player misread
 *    this?"): an exit painted near the frame's edge READS as a side wall,
 *    one near the center reads as the wall ahead. Hence the asymmetry — the
 *    ahead band is loose (the center FAMILY, `center-left`/`center`/
 *    `center-right`), because horizontal position within the far wall
 *    carries no directional meaning; the sides are EXACT, because there
 *    horizontal position IS the directional signal, and loosening them
 *    would reopen the stairwell bug immediately. `absent` is always
 *    allowed. A declared north exit at `right` while facing north is
 *    rejected by construction (the stairwell bug, caught mechanically —
 *    and the checkpoint's, the rule's second catch in its first round).
 * 3. **Shared-plate coherence** falls out of (1) for free: a plate reused
 *    across rooms with different exit sets fails — a useful accident that
 *    prevents reuse across rooms that are not actually the same shape.
 *
 * Non-cardinal tokens (`up`, `down`, `in`, `out`, and any pack-declared
 * sibling) skip the orientation arithmetic and are checked for existence
 * only — no rotation makes `up` a horizontal band.
 *
 * What this deliberately cannot catch, named rather than papered (Calder's
 * §3.4): a plate can DRAW a doorway nobody declared. The schema checks
 * declarations against the room, never the painting against the declaration
 * — the identical hole `depicts` has, inherent to validating a picture by
 * proxy. Human review remains the only gate on it.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition, FramePosition, ViewpointFacing } from '@orpheus/content-schema';

import { imageReferencesOf } from './image-validation.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { brokenReference } from './semantic-validation.js';

/** The four cardinals the rotation table turns. */
const CARDINAL_TOKENS = ['east', 'north', 'south', 'west'] as const;

type CardinalToken = (typeof CARDINAL_TOKENS)[number];

/** Is this direction one the rotation table can turn? */
const isCardinalToken = (directionToken: string): directionToken is CardinalToken =>
	(CARDINAL_TOKENS as readonly string[]).includes(directionToken);

/**
 * The rotation table — for each facing, where each cardinal sits relative to
 * the camera. One table, sixteen entries, no trigonometry: `ahead` is the
 * far wall, `right`/`left` are the frame's edges, `back` is the camera's
 * own shoulder.
 */
const RELATIVE_BY_FACING: Readonly<Record<ViewpointFacing, Readonly<Record<CardinalToken, 'ahead' | 'back' | 'left' | 'right'>>>> = {
	east: { east: 'ahead', north: 'left', south: 'right', west: 'back' },
	north: { east: 'right', north: 'ahead', south: 'back', west: 'left' },
	south: { east: 'left', north: 'back', south: 'ahead', west: 'right' },
	west: { east: 'back', north: 'right', south: 'left', west: 'ahead' },
};

/** The bands each relative direction may honestly occupy (`absent` is always allowed on top). */
const ALLOWED_BANDS: Readonly<Record<'ahead' | 'back' | 'left' | 'right', readonly FramePosition[]>> = {
	ahead: ['center', 'center-left', 'center-right'],
	back: ['behind'],
	left: ['left'],
	right: ['right'],
};

/**
 * Run the plate-geometry checks over every image that declares a viewpoint.
 *
 * @param contentPack - The parsed, schema-valid pack
 * @returns Every geometry failure found (empty = geometrically coherent)
 */
export const validateImageGeometry = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	// Every room that names each plate, gathered once — the completeness and
	// shared-plate checks both read this walk.
	const namingRoomsByImageId = new Map<string, { readonly exitTokens: ReadonlySet<string>; readonly roomId: string }[]>();

	contentPack.rooms.forEach((room, roomIndex) => {
		const exitTokens = new Set(room.exits.map((exit) => exit.directionToken));

		for (const { imageId } of imageReferencesOf(room, roomIndex)) {
			const namingRooms = namingRoomsByImageId.get(imageId) ?? [];

			namingRooms.push({ exitTokens, roomId: room.roomId });
			namingRoomsByImageId.set(imageId, namingRooms);
		}
	});

	contentPack.images.forEach((image, imageIndex) => {
		// Captured so the narrowing survives the inner closures.
		const { viewpoint } = image;

		if (viewpoint === undefined) {
			return;
		}

		const viewpointPath = `images[${String(imageIndex)}].viewpoint`;
		const declaredTokens = new Set<string>();

		viewpoint.exits.forEach((viewpointExit, exitIndex) => {
			const exitPath = `${viewpointPath}.exits[${String(exitIndex)}]`;

			if (declaredTokens.has(viewpointExit.directionToken)) {
				failures.push(
					brokenReference(exitPath, viewpointExit.directionToken, 'duplicate viewpoint direction — one exit, one place in the frame'),
				);
			}
			declaredTokens.add(viewpointExit.directionToken);

			// Check 2 — orientation, cardinals only; `absent` is always a
			// decision the author may make.
			if (isCardinalToken(viewpointExit.directionToken) && viewpointExit.framePosition !== 'absent') {
				const relativeDirection = RELATIVE_BY_FACING[viewpoint.facing][viewpointExit.directionToken];
				const allowedBands = ALLOWED_BANDS[relativeDirection];

				if (!allowedBands.includes(viewpointExit.framePosition)) {
					failures.push(
						brokenReference(
							exitPath,
							viewpointExit.directionToken,
							`facing ${viewpoint.facing}, a ${viewpointExit.directionToken} exit sits ${relativeDirection} (${allowedBands.join('/')}) — declared at "${viewpointExit.framePosition}"`,
						),
					);
				}
			}
		});

		// Check 1 — referential both directions, per naming room; check 3
		// (shared-plate coherence) falls out of running it for EVERY room.
		for (const namingRoom of namingRoomsByImageId.get(image.imageId) ?? []) {
			for (const declaredToken of declaredTokens) {
				if (!namingRoom.exitTokens.has(declaredToken)) {
					failures.push(
						brokenReference(
							viewpointPath,
							declaredToken,
							`the viewpoint declares "${declaredToken}", which is not an exit of "${namingRoom.roomId}" — a room that names this plate`,
						),
					);
				}
			}
			for (const exitToken of namingRoom.exitTokens) {
				if (!declaredTokens.has(exitToken)) {
					failures.push(
						brokenReference(
							viewpointPath,
							exitToken,
							`"${namingRoom.roomId}" has a "${exitToken}" exit this viewpoint does not place — completeness is required; declare it, or declare it "absent"`,
						),
					);
				}
			}
		}
	});

	return failures;
};
